// src/components/VideoLanding.js
import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { db, auth, storage } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SocialLinksDisplay from './SocialLinksDisplay';
import VideoProxy from './VideoProxy';
import { logVideoView } from '../utils/activityLogger';
import { getPublicProfile } from '../utils/publicProfile';

function VideoLanding({ userId: propUserId, videoId: propVideoId }) {
  const params = useParams();
  const userId = propUserId || params.userId;
  const videoId = propVideoId || params.videoId;
  
  const [videoData, setVideoData] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showProfile, setShowProfile] = useState(true);
  const location = useLocation();

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setIsAuthenticated(!!user);
    });
    return () => unsubscribe();
  }, []);

  // Fetch video data from Firestore
  useEffect(() => {
    const fetchVideo = async () => {
      if (!userId || !videoId) {
        setLoading(false);
        return;
      }
      try {
        const videoDocRef = doc(db, 'videos', videoId);
        const videoSnap = await getDoc(videoDocRef);
        if (videoSnap.exists()) {
          const data = videoSnap.data();
          setVideoData(data);
          // Log the view event
          logVideoView(videoId, userId);
        }
      } catch (error) {
        console.error('Error fetching video:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchVideo();
  }, [userId, videoId]);

  // Fetch creator's public profile projection, with migration fallback.
  useEffect(() => {
    const fetchProfile = async () => {
      if (!userId) return;
      try {
        const data = await getPublicProfile(userId);
        if (!data) return;

        setProfileData(data);

        if (data.profilePic) {
          if (data.profilePic.startsWith('http')) {
            setProfileImage(data.profilePic);
          } else {
            try {
              const profilePicRef = storageRef(storage, `profilePics/${userId}/profile.jpg`);
              const url = await getDownloadURL(profilePicRef);
              setProfileImage(url);
            } catch (e) {
              setProfileImage('/default-thumbnail.jpg');
            }
          }
        } else {
          setProfileImage('/default-thumbnail.jpg');
        }
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };
    fetchProfile();
  }, [userId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <ClipLoader size={50} color="#1E40AF" loading={loading} />
      </div>
    );
  }

  if (!videoData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
        <div className="bg-white p-8 rounded-xl shadow-md text-center max-w-md">
          <div className="text-red-500 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Video Not Found</h2>
          <p className="text-gray-600 mb-6">The video you're looking for doesn't exist or has been removed.</p>
          <a href="/" className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors">
            Go to Homepage
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-gray-50 to-gray-100 min-h-screen">
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 pt-16 pb-20 px-6 text-center">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-6 max-w-3xl mx-auto">
          {videoData.title}
        </h1>
      </div>
      
      <div className="container mx-auto px-4 -mt-10 relative z-10 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className={showProfile ? "lg:col-span-2" : "lg:col-span-3"}>
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-gray-900 w-full aspect-video">
                <VideoProxy 
                  videoUrl={videoData.videoUrl}
                  thumbnailUrl={videoData.thumbnailUrl}
                  autoPlay={!showProfile}
                />
              </div>
              
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">About This Introduction</h2>
                  <button onClick={() => setShowProfile(!showProfile)} className="text-sm text-blue-600 hover:text-blue-800">
                    {showProfile ? "Hide Profile" : "Show Profile"}
                  </button>
                </div>
                <p className="text-gray-600 mb-6 italic">
                  Created {videoData.createdAt ? new Date(videoData.createdAt.seconds * 1000).toLocaleDateString() : 'recently'}
                </p>
                <div className="border-t pt-6 text-center">
                  <a href={videoData.videoUrl} download className="text-blue-600 hover:text-blue-800 text-sm flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download video
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          {showProfile && (
            <div>
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="p-6">
                  <div className="flex flex-col items-center text-center">
                    <img
                      src={profileImage || '/default-thumbnail.jpg'}
                      alt="Profile"
                      className="w-24 h-24 rounded-full border-4 border-gray-50 shadow-md mb-4 object-cover"
                    />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                      {profileData?.fullName || 'Video Creator'}
                    </h2>
                    <p className="text-gray-600 mb-6">{profileData?.bio || 'Professional Video Introduction'}</p>
                  </div>
                  {profileData?.socialLinks?.length > 0 && (
                    <div className="mt-6 border-t pt-6">
                      <SocialLinksDisplay socialLinks={profileData.socialLinks} />
                    </div>
                  )}
                </div>
                <div className="bg-blue-50 p-6 text-center">
                  <p className="text-blue-700 text-sm">
                    Connect with {profileData?.fullName || 'the creator'} above.
                  </p>
                </div>
              </div>
              <div className="mt-6 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg shadow-lg p-6 text-white text-center">
                <h3 className="text-lg font-semibold mb-2">Want your own intro?</h3>
                <a href="/" className="block mt-4 bg-white text-purple-700 font-medium px-4 py-2 rounded-lg hover:bg-purple-50">
                  Create Your Own ReelIntro
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default VideoLanding;
